import React from 'react';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { componentByTypename } from '@/ui/utils/componentByTypename';
import { usePathname, useSearchParams } from 'next/navigation';
import { KeyValuePair } from '@/integrations/crystallize/types/KeyValuePair';
import { DoorKnobOption } from './door-knob-option/door-knob-option';
import { useSmartLockContext } from '../../../../../providers/smartlockProvider';
import { DoorKnobSectionCrystallizeData } from './types/DoorKnobSectionCrystallizeData';

import { useManufacturerSelection } from './utils/useManufacturerSelection';
import { useDoorKnobInitialization } from './utils/useDoorKnobInitializaction';
import { useUpdateKnobUrlParam } from './utils/updateKnobUrlParam';
import ManufacturerSelect from '../../ManufacturerSelect';

interface DoorKnobSectionProps {
  data: DoorKnobSectionCrystallizeData;
}

function DoorKnobSection({ data }: Readonly<DoorKnobSectionProps>) {
  const searchParams = useSearchParams();
  const currentPath = usePathname();
  const updateKnobUrlParam = useUpdateKnobUrlParam();

  const smartLockContext = useSmartLockContext();
  const { hasDoorKnob, setHasDoorKnob } = smartLockContext;
  const { selectedManufacturer, setSelectedManufacturer } = smartLockContext;

  const { manufacturerOptions, doorKnobDocument } = useDoorKnobInitialization(data);
  useManufacturerSelection(
    manufacturerOptions,
    searchParams,
    setSelectedManufacturer,
    setHasDoorKnob,
  );

  if (!doorKnobDocument) {
    return null;
  }

  const sectionHeading = componentByTypename(doorKnobDocument.heading?.content, 'RichTextContent');
  const manufacturerHeading = componentByTypename(
    doorKnobDocument.manufacturerHeading?.content,
    'RichTextContent',
  );
  const yesImage = componentByTypename(
    doorKnobDocument.yesImage?.content,
    'ImageContent',
  )?.firstImage; // items. items.find((comp) => comp.id === 'yes-image')?.content.firstImage;
  const noImage = componentByTypename(
    doorKnobDocument.noImage?.content,
    'ImageContent',
  )?.firstImage; // items.find((comp) => comp.id === 'no-image')?.content.firstImage;

  const onDoorKnobSelection = (selectedManufacturerOption?: KeyValuePair) => {
    const selectedAdapter = manufacturerOptions.find(
      (option) => option.value === selectedManufacturerOption?.value,
    );

    setSelectedManufacturer(selectedAdapter);
    updateKnobUrlParam(currentPath, !!selectedManufacturerOption, selectedAdapter);
  };

  const onHasDoorKnobSelection = (doorKnobSelected: boolean) => {
    setHasDoorKnob(doorKnobSelected);
    updateKnobUrlParam(currentPath, doorKnobSelected, selectedManufacturer);
  };

  return (
    <div>
      <RichTextRenderer className="text-xl" blocks={sectionHeading} />
      <div className="my-4 flex justify-center gap-4">
        <DoorKnobOption
          image={yesImage}
          isSelected={hasDoorKnob}
          onSelect={() => onHasDoorKnobSelection(true)}
        />
        <DoorKnobOption
          image={noImage}
          isSelected={!hasDoorKnob}
          onSelect={() => onHasDoorKnobSelection(false)}
        />
      </div>
      {hasDoorKnob && (
        <ManufacturerSelect
          heading={manufacturerHeading}
          options={manufacturerOptions.map((option) => ({
            key: option.label,
            value: option.value,
          }))}
          selectedOption={
            selectedManufacturer
              ? { key: selectedManufacturer.label, value: selectedManufacturer.value }
              : undefined
          }
          onChange={onDoorKnobSelection}
        />
      )}
    </div>
  );
}

export default DoorKnobSection;
